import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FiTarget,
  FiEye,
  FiAward,
  FiUsers,
  FiCheckCircle,
  FiArrowRight,
  FiShield,
  FiTrendingUp,
  FiGlobe,
  FiPackage,
  FiTruck,
  FiCpu,
} from 'react-icons/fi';
import { BsDroplet, BsRecycle, BsShield, BsLightning } from 'react-icons/bs';
import { GiChemicalDrop, GiFactory } from 'react-icons/gi';

// Import team member images
import vrushaliImage from '../assets/vrushali.png';
import pradhnyaImage from '../assets/pradhnya.png';
import arunaImage from '../assets/aruna.png';
import jitenImage from '../assets/jiten.png';
import ranjitImage from '../assets/ranjit.png';
import sanjayImage from '../assets/sanjay.png';

const About = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const founders = [
    {
      name: 'Vrushali Pradhan',
      role: 'Managing Director',
      subtitle: 'Legal and Strategies',
      description: 'Practicing lawyer for over 20+ years. She is responsible for developing business strategies for VP Chem Technologies.',
      color: 'from-amber-500 to-orange-500',
      icon: '⚖️',
      image: vrushaliImage,
    },
    {
      name: 'Mrs. Pradhnya Lambe',
      role: 'Partner/Director',
      subtitle: 'Business Operations',
      description: 'A businesswoman in the trading industry. She handles documentation and other related work for the LLP.',
      color: 'from-blue-500 to-cyan-500',
      icon: '📋',
      image: pradhnyaImage,
    },
    {
      name: 'Mrs. Aruna Vyas',
      role: 'Partner/Director',
      subtitle: 'Administration & Client Relations',
      description: 'A businesswoman with experience in the stone business. She handles office administration and the clients portfolio.',
      color: 'from-green-500 to-emerald-500',
      icon: '👥',
      image: arunaImage,
    },
  ];

  const coreTeam = [
    {
      name: 'Mr. Jiten A. Pradhan',
      role: 'Director - Administration',
      description: 'A dynamic leader with a proven track record in managing administration, media, and strategic planning.',
      icon: '🎯',
      image: jitenImage,
    },
    {
      name: 'Mr. Ranjit Lambe',
      role: 'Director - Technical',
      description: 'Holds technical expertise in innovation. He is responsible for technical operations and advancing the company\'s technical capabilities.',
      icon: '⚙️',
      image: ranjitImage,
    },
    {
      name: 'Mr. Sanjay Vyas',
      role: 'Director - Operations',
      description: 'Ensures the seamless delivery of high-quality water base solution and rigorous quality standards, driving the company\'s growth.',
      icon: '📊',
      image: sanjayImage,
    },
  ];

  const whyChooseUs = [
    {
      title: 'Global Partnerships',
      desc: 'Partnered with top companies to ensure consistent quality and supply',
      icon: FiGlobe,
      gradient: 'from-blue-500 to-cyan-500',
    },
    {
      title: 'Comprehensive Product Range',
      desc: 'Catering to industries such as oil, petroleum, agriculture, and horticulture',
      icon: FiPackage,
      gradient: 'from-green-500 to-emerald-500',
    },
    {
      title: 'Quality Assurance',
      desc: 'Adherence to international standards through rigorous quality checks',
      icon: FiShield,
      gradient: 'from-purple-500 to-pink-500',
    },
    {
      title: 'Efficient Supply Chain',
      desc: 'Ensuring timely delivery with optimized logistics',
      icon: FiTruck,
      gradient: 'from-orange-500 to-red-500',
    },
    {
      title: 'Technical Expertise',
      desc: 'Dedicated team offering product support and application expertise',
      icon: FiCpu,
      gradient: 'from-indigo-500 to-blue-500',
    },
  ];

  // Molecular pattern component
  const MolecularPattern = () => (
    <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <pattern id="molecule-about" x="0" y="0" width="200" height="200" patternUnits="userSpaceOnUse">
          <circle cx="20" cy="20" r="3" fill="currentColor" opacity="0.3" />
          <circle cx="80" cy="40" r="4" fill="currentColor" opacity="0.2" />
          <circle cx="140" cy="70" r="2" fill="currentColor" opacity="0.4" />
          <circle cx="180" cy="120" r="3" fill="currentColor" opacity="0.25" />
          <line x1="20" y1="20" x2="80" y2="40" stroke="currentColor" strokeWidth="0.5" opacity="0.2" />
          <line x1="80" y1="40" x2="140" y2="70" stroke="currentColor" strokeWidth="0.5" opacity="0.2" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#molecule-about)" />
    </svg>
  );

  return (
    <div className="min-h-screen overflow-hidden bg-vp-cream-50">
      {/* Hero Section - Page Header */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden pt-32">
        {/* Dynamic Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-vp-cream-100 via-vp-cream-200 to-white">
          <div 
            className="absolute inset-0 opacity-20"
            style={{
              background: `radial-gradient(circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(0, 135, 81, 0.15), transparent 50%)`,
            }}
          />
        </div>

        {/* Molecular Pattern */}
        <div className="absolute inset-0 text-vp-green-500 opacity-10">
          <MolecularPattern />
        </div>

        {/* Floating molecules */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(10)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-4 h-4 rounded-full bg-gradient-to-br from-vp-green-400/30 to-vp-blue-500/30 blur-sm"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -50, 0],
                x: [0, Math.random() * 50 - 25, 0],
                scale: [1, 1.2, 1],
              }}
              transition={{
                duration: 5 + Math.random() * 3,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
        </div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-4 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              className="inline-flex items-center space-x-3 bg-white/80 backdrop-blur-xl px-6 py-3 rounded-full text-vp-grey-800 mb-8 border border-vp-green-500/30"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <GiChemicalDrop className="text-vp-blue-600 text-2xl" />
              <span className="font-semibold">Leading water based solution Trading Company</span>
            </motion.div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-vp-grey-900 mb-6 leading-tight">
              <span className="block mb-2">Leading water based solution</span>
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-vp-green-600 via-vp-blue-600 to-vp-green-600">
                Trading Company
              </span>
            </h1>

            <motion.p
              className="text-2xl md:text-3xl text-black mb-12 max-w-4xl mx-auto leading-relaxed"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <span className="text-vp-green-600 font-semibold">We are partners in a clean and more efficient future.</span>
            </motion.p>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-8 h-14 border-2 border-vp-grey-400 rounded-full flex justify-center p-2 bg-white/50">
            <motion.div 
              className="w-2 h-4 bg-gradient-to-b from-vp-green-600 to-vp-blue-600 rounded-full"
              animate={{ y: [0, 20, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          </div>
        </motion.div>
      </section>

      {/* Our Story and Identity Section */}
      <section className="py-16 md:py-20 bg-vp-cream-50 relative overflow-hidden bg-pattern-dots">
        <div className="absolute inset-0 bg-gradient-to-br from-vp-cream-100 via-white to-vp-cream-100" />
        
        <div className="relative container mx-auto px-4 lg:px-8">
          <motion.div
            className="max-w-6xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {/* Section Header */}
            <div className="text-center mb-16">
              <motion.span
                className="inline-block px-6 py-2 bg-gradient-to-r from-vp-green-100 to-vp-blue-100 rounded-full text-vp-green-700 font-semibold mb-6 shadow-md"
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ type: "spring" }}
              >
                🧪 Our Story & Identity
              </motion.span>
              
              <h2 className="text-5xl md:text-6xl font-black text-vp-grey-900 mb-8">
                Bridging Innovation &{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-vp-green-600 to-vp-blue-600">
                  Industry Needs
                </span>
              </h2>
            </div>

            {/* Story Content Grid */}
            <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
              {/* Image/Visual Placeholder */}
              <motion.div
                className="relative group"
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <div className="aspect-square rounded-3xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&h=800&fit=crop"
                    alt="VP Chem Technologies Team"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </motion.div>

              {/* Story Text */}
              <motion.div
                className="space-y-6"
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <div className="bg-white rounded-2xl p-8 border-2 border-vp-green-200 shadow-md">
                  <div className="flex items-start space-x-4 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-vp-green-600 to-vp-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <FiCheckCircle className="text-white text-2xl" />
                    </div>
                    <p className="text-lg text-black leading-relaxed">
                      <span className="font-bold text-vp-grey-900">VP Chem Technologies LLP</span> is a distinguished entity in the{' '}
                      <span className="text-vp-green-600 font-semibold">biodegradable water base solution trading industry</span>. We pride ourselves on being a{' '}
                      <span className="text-vp-blue-600 font-semibold">leading company</span>, committed to driving positive change through responsible and efficient business practices.
                    </p>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-vp-blue-50 to-vp-green-50 rounded-2xl p-8 border border-vp-blue-200">
                  <p className="text-lg text-black leading-relaxed">
                    We act as a <span className="font-semibold text-vp-grey-900">vital bridge between innovation and industry needs</span>. We deliver high-quality products and customized solutions by partnering with reputable manufacturers, ensuring seamless operations for our clients across the globe.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-vp-green-50 to-vp-blue-50 rounded-2xl p-8 border border-vp-green-200">
                  <p className="text-lg text-black leading-relaxed">
                    Our collaboration with the manufacturer allows us to offer cutting-edge solutions tailored to industry demands. This collaboration strengthens our market presence, nationally and internationally, through shared expertise and insights. We champion innovation that helps to clean the environment.
                  </p>
                </div>
              </motion.div>
            </div>

            {/* CTA Button */}
            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <a
                href="#certifications"
                className="inline-flex items-center space-x-3 px-10 py-5 bg-gradient-to-r from-vp-green-600 to-vp-blue-600 text-white font-bold text-lg rounded-full hover:shadow-xl transform hover:scale-105 transition-all duration-300"
              >
                <FiShield className="text-2xl" />
                <span>View Our Manufacturing Team's Certifications</span>
                <FiArrowRight className="group-hover:translate-x-2 transition-transform" />
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Vision & Mission Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-vp-grey-700 via-vp-grey-600 to-vp-grey-700 text-white relative overflow-hidden bg-pattern-hexagon">
        {/* Animated blobs */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-96 h-96 bg-vp-green-500/10 rounded-full blur-3xl animate-blob" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-vp-blue-500/10 rounded-full blur-3xl animate-blob" style={{ animationDelay: '2s' }} />
        </div>

        <div className="relative container mx-auto px-4 lg:px-8">
          <motion.div
            className="max-w-6xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {/* Section Header */}
            <div className="text-center mb-16">
              <motion.span
                className="inline-block px-6 py-2 bg-white/10 backdrop-blur-xl rounded-full text-vp-green-400 font-semibold mb-6 border border-vp-green-400/30"
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ type: "spring" }}
              >
                🎯 Our Vision & Mission
              </motion.span>
              
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Driving Excellence Through{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-vp-green-400 to-vp-blue-400">
                  Collaboration
                </span>
              </h2>
            </div>

            {/* Vision & Mission Cards */}
            <div className="grid md:grid-cols-2 gap-8 mb-16">
              {/* Vision Card */}
              <motion.div
                className="relative group"
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                whileHover={{ y: -10 }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-cyan-500 opacity-20 group-hover:opacity-30 blur-xl rounded-3xl transition-opacity" />
                
                <div className="relative bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-10 h-full group-hover:bg-white/10 group-hover:border-white/30 transition-all duration-500">
                  <motion.div
                    className="w-20 h-20 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center mb-6"
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.6 }}
                  >
                    <FiEye className="w-10 h-10 text-white" />
                  </motion.div>
                  
                  <h3 className="text-3xl md:text-4xl font-black text-black mb-6">Our Vision</h3>
                  <p className="text-xl text-black leading-relaxed">
                    To be the <span className="text-blue-600 font-bold">preferred partner</span> in the water base solution industry, delivering excellence and driving innovations through{' '}
                    <span className="text-cyan-600 font-bold">collaborative partnerships</span>.
                  </p>
                </div>
              </motion.div>

              {/* Mission Card */}
              <motion.div
                className="relative group"
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                whileHover={{ y: -10 }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-green-500 to-emerald-500 opacity-20 group-hover:opacity-30 blur-xl rounded-3xl transition-opacity" />
                
                <div className="relative bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-10 h-full group-hover:bg-white/10 group-hover:border-white/30 transition-all duration-500">
                  <motion.div
                    className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center mb-6"
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.6 }}
                  >
                    <FiTarget className="w-10 h-10 text-white" />
                  </motion.div>
                  
                  <h3 className="text-3xl md:text-4xl font-black text-black mb-6">Our Mission</h3>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <FiCheckCircle className="text-green-600 text-2xl mt-1 flex-shrink-0" />
                      <p className="text-lg text-black leading-relaxed">
                        To create change by supplying <span className="text-green-600 font-bold">premium grade Biodegradable & innovative water base solution</span> efficiently and responsibly.
                      </p>
                    </div>
                    <div className="flex items-start space-x-3">
                      <FiCheckCircle className="text-green-600 text-2xl mt-1 flex-shrink-0" />
                      <p className="text-lg text-black leading-relaxed">
                        To empower our clients with <span className="text-emerald-600 font-bold">tailor made solutions</span> with unmatched service.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* CTA Button */}
            <motion.div
              className="text-center"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
            >
              <Link
                to="/products"
                className="group inline-flex items-center space-x-3 px-10 py-5 bg-white text-slate-900 font-bold text-lg rounded-full hover:bg-gradient-to-r hover:from-green-400 hover:to-blue-400 hover:text-white transform hover:scale-105 transition-all duration-300 shadow-2xl"
              >
                <span>See How We Deliver Our Mission</span>
                <FiArrowRight className="group-hover:translate-x-2 transition-transform" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-vp-cream-50 via-white to-vp-cream-100 relative overflow-hidden bg-pattern-grid">
        <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-gradient-to-bl from-vp-green-100/30 to-transparent rounded-full blur-3xl" />
        
        <div className="relative container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {/* Section Header */}
            <div className="text-center mb-16">
              <motion.span
                className="inline-block px-6 py-2 bg-gradient-to-r from-green-100 to-blue-100 rounded-full text-green-700 font-semibold mb-6"
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ type: "spring" }}
              >
                ⭐ Why Choose Us
              </motion.span>
              
              <h2 className="text-4xl md:text-5xl font-bold text-vp-grey-900 mb-6">
                Your Trusted Partner in{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-vp-green-600 to-vp-blue-600">
                  Water Base Solution Excellence
                </span>
              </h2>
              <p className="text-xl md:text-2xl text-black max-w-3xl mx-auto">
                We ensure reliability, quality, and innovation in every partnership
              </p>
            </div>

            {/* Value Proposition Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto mb-16">
              {whyChooseUs.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  whileHover={{ y: -10, scale: 1.02 }}
                  className="group relative"
                >
                  {/* Glowing background */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${item.gradient} opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500 rounded-3xl`} />
                  
                  <div className="relative bg-white rounded-3xl p-8 border-2 border-slate-200 group-hover:border-green-400 transition-all duration-500 h-full shadow-lg group-hover:shadow-2xl">
                    <motion.div
                      className={`w-16 h-16 bg-gradient-to-br ${item.gradient} rounded-2xl flex items-center justify-center mb-6`}
                      whileHover={{ rotate: 360, scale: 1.1 }}
                      transition={{ duration: 0.6 }}
                    >
                      <item.icon className="w-8 h-8 text-white" />
                    </motion.div>
                    
                    <h3 className="text-2xl font-bold text-slate-900 mb-4 group-hover:text-green-600 transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-black leading-relaxed text-lg">
                      {item.desc}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* CTA Button */}
            <motion.div
              className="text-center"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
            >
              <Link
                to="/contact"
                className="group inline-flex items-center space-x-3 px-10 py-5 bg-gradient-to-r from-green-600 to-blue-600 text-white font-bold text-lg rounded-full hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
              >
                <span>Request a Tailor-Made Solution</span>
                <FiArrowRight className="group-hover:translate-x-2 transition-transform" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Leadership Section - Founders */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-vp-grey-700 via-vp-grey-600 to-vp-grey-700 text-white relative overflow-hidden bg-pattern-grid">
        {/* Animated grid background */}
        <div className="absolute inset-0 opacity-5">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(to right, rgba(34, 197, 94, 0.3) 1px, transparent 1px),
                                linear-gradient(to bottom, rgba(34, 197, 94, 0.3) 1px, transparent 1px)`,
              backgroundSize: '50px 50px',
            }}
          />
        </div>

        <div className="relative container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {/* Section Header */}
            <div className="text-center mb-16">
              <motion.span
                className="inline-block px-6 py-2 bg-white/10 backdrop-blur-xl rounded-full text-green-400 font-semibold mb-6 border border-green-400/30"
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ type: "spring" }}
              >
                👥 Our Leadership Team
              </motion.span>
              
              <h2 className="text-5xl md:text-6xl lg:text-7xl font-black mb-6">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-blue-400">
                  Core Team
                </span>
              </h2>
              <p className="text-xl md:text-2xl text-black max-w-3xl mx-auto">
                Driven by expertise, united by vision
              </p>
            </div>

            {/* Core Team Section */}
            <div className="max-w-6xl mx-auto">
              <div className="grid md:grid-cols-3 gap-8 mb-16">
                {coreTeam.map((member, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                    whileHover={{ scale: 1.05 }}
                    className="group"
                  >
                    <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 group-hover:bg-white/10 group-hover:border-green-400/50 transition-all duration-300 h-full">
                      <div className="w-24 h-24 mx-auto mb-4 overflow-hidden rounded-full shadow-lg">
                        <img 
                          src={member.image} 
                          alt={member.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <h4 className="text-xl font-bold text-black mb-2 text-center">
                        {member.name}
                      </h4>
                      <p className="text-green-600 font-semibold mb-4 text-center text-sm">
                        {member.role}
                      </p>
                      <p className="text-black text-sm leading-relaxed text-center">
                        {member.description}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* CTA Button */}
            <motion.div
              className="text-center"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
            >
              <Link
                to="/contact"
                className="group inline-flex items-center space-x-3 px-10 py-5 bg-white text-slate-900 font-bold text-lg rounded-full hover:bg-green-400 hover:text-white transform hover:scale-105 transition-all duration-300 shadow-2xl"
              >
                <FiUsers className="text-2xl" />
                <span>Connect with Our Team</span>
                <FiArrowRight className="group-hover:translate-x-2 transition-transform" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Certifications Section */}
      <section id="certifications" className="py-16 md:py-20 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-green-50/50 via-white to-blue-50/50" />
        
        <div className="relative container mx-auto px-4 lg:px-8">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <motion.span
              className="inline-block px-6 py-2 bg-gradient-to-r from-green-100 to-blue-100 rounded-full text-green-700 font-semibold mb-6"
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ type: "spring" }}
            >
              🏆 Quality & Certifications
            </motion.span>
            
            <h2 className="text-5xl md:text-6xl font-black text-slate-900 mb-6">
              Certified Excellence Through{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-blue-600">
                Our Partners
              </span>
            </h2>
            
            <div className="max-w-4xl mx-auto bg-gradient-to-br from-green-50 to-blue-50 rounded-3xl p-12 border-2 border-green-200 mt-16">
              <GiFactory className="w-24 h-24 mx-auto text-green-600 mb-6" />
              <h3 className="text-3xl font-bold text-slate-900 mb-4">
                Manufacturing Team
              </h3>
              <p className="text-lg text-black leading-relaxed mb-8">
                Our manufacturing team maintains the highest international standards with comprehensive certifications including ISO 9001, ISO 14001, GMP, and REACH compliance.
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {['ISO 9001', 'ISO 14001', 'GMP', 'REACH'].map((cert, i) => (
                  <motion.div
                    key={i}
                    className="bg-white rounded-xl p-6 shadow-lg"
                    whileHover={{ scale: 1.05, rotate: 5 }}
                    transition={{ duration: 0.3 }}
                  >
                    <FiShield className="w-12 h-12 mx-auto text-green-600 mb-3" />
                    <p className="font-bold text-slate-900">{cert}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;



